import http from '../http-common';

const API_URL_PATH = 'folder-runs';

/** Drops undefined / null / '' entries so optional filters never reach the query string. */
function cleanParams(params = {}) {
  const query = {};
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') query[key] = value;
  });
  return query;
}

function runPath(runId, suffix = '') {
  return `${API_URL_PATH}/${encodeURIComponent(runId)}${suffix}`;
}

/**
 * Client for Folder Runs (spec 17-09): **Run** = one folder + one configuration + one engine
 * version executed at a point in time. A run launches nothing new — it fans out the existing
 * per-video re-analysis path under its own `batch` — so this client only creates, reads and
 * compares the aggregate.
 *
 * Response shapes follow the contract: the list returns `{ items: FolderRunSummary[], total }`,
 * `get()` returns a FolderRunSummary plus `videos: FolderRunVideo[]`, and launching returns
 * 202 `{ id, batch, accepted: number[], rejected: [{ id, reason }] }`.
 */
class FolderRunsService {
  /**
   * Runs ordered by `created_at` DESC.
   * @param {{ folder_id?: number|string, limit?: number, offset?: number }} params
   * @returns {Promise} resolves `{ items: FolderRunSummary[], total }`
   */
  list(params = {}) {
    return http.get(API_URL_PATH, { params: cleanParams(params) });
  }

  /**
   * One run with its per-video table.
   * @param {number|string} runId
   * @returns {Promise} resolves `FolderRunSummary & { videos: FolderRunVideo[] }`
   */
  get(runId) {
    return http.get(runPath(runId));
  }

  /**
   * Launches a run over every video currently in the folder. `promote_on_success` is always
   * false server-side: measuring must not change what users see.
   *
   * @param {{ folder_id: number|string, config_id?: number|null, config_version_id?: string|null,
   *   engines?: string[], name?: string, note?: string, socketId?: string }} payload
   * @returns {Promise} 202 `{ id, batch, accepted: number[], rejected: [{ id, reason }] }`
   */
  create(payload = {}) {
    const body = { folder_id: Number(payload.folder_id) };
    // Lets the worker push progress to this browser tab; polling still covers it when absent.
    if (typeof payload.socketId === 'string' && payload.socketId) body.socketId = payload.socketId;
    if (payload.config_id !== undefined && payload.config_id !== null) {
      body.config_id = Number(payload.config_id);
    }
    if (payload.config_version_id) body.config_version_id = payload.config_version_id;
    if (Array.isArray(payload.engines) && payload.engines.length) {
      body.engines = [...payload.engines];
    }
    if (typeof payload.name === 'string' && payload.name.trim()) body.name = payload.name.trim();
    if (typeof payload.note === 'string' && payload.note.trim()) body.note = payload.note.trim();
    return http.post(API_URL_PATH, body);
  }

  /**
   * Runs the SAME frozen member snapshot and configuration version again with whatever engine
   * version is deployed now — the one comparison that isolates the engine change.
   * @param {number|string} runId
   * @param {{ note?: string, socketId?: string }} payload
   * @returns {Promise} 202, same body as `create`
   */
  rerun(runId, payload = {}) {
    const body = {};
    if (typeof payload.note === 'string' && payload.note.trim()) body.note = payload.note.trim();
    if (typeof payload.socketId === 'string' && payload.socketId) body.socketId = payload.socketId;
    return http.post(runPath(runId, '/rerun'), body);
  }

  /** Deletes the run row only; the per-video analyses and their results survive. */
  remove(runId) {
    return http.delete(runPath(runId));
  }

  /**
   * Two runs side by side with their comparability verdict.
   * @param {number|string} a
   * @param {number|string} b
   * @returns {Promise} resolves `{ a, b, comparability: { verdict, differing, reason }, delta,
   *   per_video }`
   */
  compare(a, b) {
    return http.get(`${API_URL_PATH}/compare`, { params: { a, b } });
  }
}

export default new FolderRunsService();
